import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box, CssBaseline, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Collapse, Typography,Menu, MenuItem, Avatar, Divider,Tooltip } from '@mui/material';
import { Logout, Lock, AccountCircle } from '@mui/icons-material';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import { Icon } from "@iconify/react";
import { Link, useLocation } from 'react-router-dom';
import { navConfig } from './Menuitem';
import { useDispatch, useSelector } from "react-redux";
import { clearUploadedDocument, toggleFullView } from "../../store/reducers/uploadDocumentSlice";
import ProfileMenu from './ProfileMenu';
import { useLogger } from '../../hooks/useLogger';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: open ? drawerWidth : 100,
  // width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 65px)`,
  width: '100%',
  height: '80px'
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidth : 100,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : 100,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    border: 'none',
    overflowX: 'hidden',
  },
}));

const ToggleButton = styled(IconButton)(({ theme, open }) => ({
  transition: theme.transitions.create(['left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  position: 'fixed',
  left: open ? '8.6em' : '3.2em',
  top: '4em',
  background: '#fff',
  border: "5px solid #f9f9f9",
  width: '55px',
  height: '55px',
  '&:hover': {
    background: '#fff',
  },
  zIndex: 2,
}));

export default function MiniDrawer({ open, setOpen, openSideBar, setOpenSideBar }) {
  const logEvent = useLogger();
  const location = useLocation();
  const dispatch = useDispatch();
  const { uploadedDocument} = useSelector((state) => state.uploadDocument);
  const { profileData } = useSelector((state) => state.profile);
  
  const [openSubmenu, setOpenSubmenu] = React.useState(() => {
    const initialState = {};
    navConfig.forEach((item) => {
      if (item.children && item.children.some(subItem => subItem.path === location.pathname)) {
        initialState[item.id] = true;
      }
    });
    return initialState;
  });

  // Ensure submenu opens when a subitem is selected
  React.useEffect(() => {
    setOpenSubmenu((prev) => {
      const newState = { ...prev };
      navConfig.forEach((item) => {
        if (item.children && item.children.some(subItem => subItem.path === location.pathname)) {
          newState[item.id] = true;
        }
      });
      return newState;
    });
  }, [location.pathname]);
  
  const handleToggleSubmenu = (id) => {
    if(open)
    {
      setOpenSubmenu((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    }
  };

  return (
    (!uploadedDocument ||
      (uploadedDocument?.data?.document_id && !uploadedDocument?.fullView) ||
      (uploadedDocument?.data?.document_id)) && (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ background: "#fff", color: "#000", boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgb(209, 213, 219) 0px 0px 0px 0px inset" }}>
        <Toolbar sx={{ height: "80px", display: "flex", justifyContent: "space-between", }}>
          { uploadedDocument?.fullView === true ? 
            <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 1, alignItems: 'center', width: "100%" }}>
              <Box sx={{ display: "flex", cursor: "pointer" }} onClick={() => { logEvent('Click', 'back to new-esign from pdf-editor', 'new-esign/pdfeditor'); dispatch(clearUploadedDocument()); dispatch(toggleFullView()); setOpenSideBar(true) }} >
                <svg width="11" height="20" viewBox="0 0 11 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L2 10L9 2" stroke="#2556AB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <Typography noWrap component="p" sx={{ fontSize: '20px', fontWeight: 600, lineHeight: '20px', color: "#1D1D1D", ml: 2 }}>
                <Tooltip title={uploadedDocument?.data?.document_name}>
                  <span>
                    {(() => {
                      const fullName = uploadedDocument?.data?.document_name || "";
                      const dotIndex = fullName.lastIndexOf(".");
                      const extension = dotIndex !== -1 ? fullName.slice(dotIndex) : "";
                      const baseName = dotIndex !== -1 ? fullName.slice(0, dotIndex) : fullName;
                      return baseName.length > 20 ? baseName.slice(0, 20) + "..." + extension : fullName;
                    })()}
                  </span>
                </Tooltip>
                </Typography>
              </Box>
              <ProfileMenu />
            </Box>
            :
            <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 1, alignItems: 'center', width: "100%", }}>
              <img src={`${process.env.PUBLIC_URL}/images/meon-tech.png`} alt="Logo" style={{ height: 35, cursor: "pointer" }} onClick={() => {
                logEvent('Click', 'click on logo in header', 'header');
                dispatch(clearUploadedDocument());
                window.location.replace(`${process.env.PUBLIC_URL}/esign/new-esign`);
              }}  />
              <ProfileMenu />
            </Box>
          }
        </Toolbar>
      </AppBar>
      {(!uploadedDocument || !uploadedDocument?.data?.document_id || (location.pathname !== "/esign/new-esign" && location.pathname !== "/esign/pdfeditor")) && (
        !location.pathname.includes("choose-template") ? 
        <Drawer variant="permanent" open={open}>
          <List sx={{marginTop: "80px"}}>
            <ToggleButton open={open} onClick={() => setOpen(!open)}>
                {open ? <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.55084 14.983L2.06779 8.49998L8.55084 2.01693" stroke="#185CB7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg> : <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.06779 14.983L8.55085 8.5L2.06779 2.01695" stroke="#185CB7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
            </ToggleButton>
            {navConfig.map((item) => {
              const isActive = location.pathname === item.path || (item.children && item.children.some(sub => sub.path === location.pathname));
              return (
                <React.Fragment key={item.id}>
                  <ListItem disablePadding sx={{ display: "block", mt: "12px" }}>
                    {item.children ? (
                      <>
                        <ListItemButton
                          sx={{ ml: open ? "21px" : "", justifyContent: open ? "initial" : "center", height: "48px" }}
                          onClick={() => {logEvent('Click', `open the dropdown of ${item.title} menu`, 'header'); handleToggleSubmenu(item.id)}}
                        >
                          <ListItemIcon sx={{ minWidth: 0, justifyContent: "center", mr: open ? "15px" : "", 
                              background: isActive && !open ? "linear-gradient(to right, #2257AD, #B01121)" : open ? "transparent":"#F4F4F4",
                              width: !open ? "50px" : 'Auto',
                              height: !open ? "50px" : 'Auto',
                              borderRadius: !open ? "320px" : '',
                              alignItems: !open ? "center" : '',
                            }}>
                            {item.icon(isActive && !open ? "#fff" : "#000000")}
                          </ListItemIcon>
                          {open && (<>
                            <ListItemText primary={item.title} sx={{ opacity: open ? 1 : 0, "& .MuiTypography-root": { fontSize: "15px", fontWeight: "500", color: isActive && !open ? "#fff" : "#1D1D1D" } }} />
                            <Icon icon={openSubmenu[item.id] ? "mdi:chevron-up" : "mdi:chevron-down"} />
                            </>
                          )}
                        </ListItemButton>
                        {open && (
                          <Collapse in={openSubmenu[item.id]} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {item.children.map((subItem) => (
                                <Link key={subItem.id} to={subItem.path} style={{ textDecoration: "none", color: "inherit" }}>
                                  <ListItemButton sx={{ ml: open ? 6.5 : 2, pl: 3, height: "48px",
                                    background: location.pathname === subItem.path ? "linear-gradient(to right, #2257AD, #B01121)" : "transparent", borderRadius: "320px 0 0 320px" }}>
                                    <ListItemText primary={subItem.title} sx={{ "& .MuiTypography-root": { fontSize: "14px", fontWeight: "400", color: location.pathname === subItem.path ? "#fff" : "#1D1D1D" } }} />
                                  </ListItemButton>
                                </Link>
                              ))}
                            </List>
                          </Collapse>
                        )}
                      </>
                    ) : (
                      <Link to={item.path} style={{ textDecoration: "none", color: "inherit", position: "relative",  }}>
                        {isActive && open && (                     
                        <Box sx={{ height: "48px", width: "27px", background: "linear-gradient(-90deg, #2257AD 0%, #B01121 100%)", position: "absolute", left: "-15px", borderRadius: "0 320px 320px 0" }} />)}
                        <ListItemButton sx={{ justifyContent: open ? "initial" : "center", height: "48px", background: location.pathname === item.path && open ? "linear-gradient(to right, #2257AD, #B01121)" : "transparent", ml: open ? "21px" : "", borderRadius: "320px 0 0 320px" }}>
                          <ListItemIcon sx={{ minWidth: 0, justifyContent: "center", mr: open ? "15px" : "", m:"",
                              background: isActive && !open ? "linear-gradient(to right, #2257AD, #B01121)" : !isActive && !open ? "#F4F4F4" : "transparent",
                              width: (isActive && !open) || (!isActive && !open) ? "50px" : 'Auto',
                              height: (isActive && !open) || (!isActive && !open) ? "50px" : 'Auto',
                              borderRadius: (isActive && !open) || (!isActive && !open) ? "320px" : '',
                              alignItems: (isActive && !open) || (!isActive && !open) ? "center" : '',
                            }}>
                          {item.icon((isActive && !open) || (isActive && open) ? "#fff" : "#000000")}
                          </ListItemIcon>
                          {open && (<ListItemText primary={item.title} sx={{ opacity: open ? 1 : 0, "& .MuiTypography-root": { fontSize: "15px", fontWeight: "500", color: location.pathname === item.path ? "#fff" : "#1D1D1D" } }} />)}
                        </ListItemButton>
                      </Link>
                    )}
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Drawer> : ""
      )}
    </Box>
    )
    
  );
}
